import { Component, Prop, Build } from '@stencil/core';

// Type definitions for Intercom
declare global {
  interface Window {
    Intercom?: any;
    intercomSettings?: any;
    __ME_GLOBALS__?: { isBot?: boolean };
  }
}

/**
 * Intercom live-chat widget loader for SignNow.
 * Renders nothing visible - injects and boots the Intercom Messenger script on the page.
 *
 * @product signnow
 * @version 1.0.0
 * @category SignNow
 * @example
 *   <sn-intercom-chat></sn-intercom-chat>
 */
@Component({
  tag: 'sn-intercom-chat',
  shadow: false,
})
export class SNIntercomChat {
  /**
   * Intercom workspace app ID
   */
  @Prop() appId: string = 'm2ad1co7';

  /**
   * Intercom API base URL
   */
  @Prop() apiBase: string = 'https://api-iam.intercom.io';

  // Loading the Messenger costs ~280 KB and ~110 ms of main-thread blocking, all of it spent
  // before the visitor has done anything. We arm the load on the first sign of engagement
  // instead: whoever ends up clicking the launcher has scrolled or tapped first, so the widget
  // is already on its way by the time they reach for it, and everyone else never pays.
  private static readonly INTERACTION_EVENTS = ['pointerdown', 'touchstart', 'keydown', 'scroll', 'mousemove'];

  private detachInteractionListeners?: () => void;

  componentDidLoad() {
    if (!Build.isBrowser) return;
    if (window.__ME_GLOBALS__?.isBot) return;

    this.armOnFirstInteraction();
  }

  disconnectedCallback() {
    this.detachInteractionListeners?.();
  }

  private armOnFirstInteraction() {
    // `passive` keeps scroll/touch off the blocking path; `once` is set per listener so the first
    // event to fire cleans itself up, and we remove the rest by hand.
    const options: AddEventListenerOptions = { passive: true, once: true };

    const onInteraction = () => {
      this.detachInteractionListeners?.();
      this.detachInteractionListeners = undefined;
      this.bootIntercom();
    };

    for (const event of SNIntercomChat.INTERACTION_EVENTS) {
      window.addEventListener(event, onInteraction, options);
    }

    this.detachInteractionListeners = () => {
      for (const event of SNIntercomChat.INTERACTION_EVENTS) {
        window.removeEventListener(event, onInteraction, options);
      }
    };
  }

  private bootIntercom() {
    if (!Build.isBrowser) return;

    window.intercomSettings = {
      api_base: this.apiBase,
      app_id: this.appId,
    };

    const w = window;
    const ic = w.Intercom;

    if (typeof ic === 'function') {
      ic('reattach_activator');
      ic('update', w.intercomSettings);
      return;
    }

    const i: any = function (...args: any[]) {
      i.c(args);
    };
    i.q = [];
    i.c = (args: any) => {
      i.q.push(args);
    };
    w.Intercom = i;

    const load = () => {
      if (document.querySelector('script[src*="widget.intercom.io/widget"]')) return;

      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.src = `https://widget.intercom.io/widget/${this.appId}`;
      const firstScript = document.getElementsByTagName('script')[0];
      firstScript.parentNode?.insertBefore(script, firstScript);
    };

    if (document.readyState === 'complete') {
      load();
    } else if ((w as any).attachEvent) {
      (w as any).attachEvent('onload', load);
    } else {
      w.addEventListener('load', load, false);
    }
  }

  render() {
    if (!Build.isBrowser) {
      return null;
    }

    return null;
  }
}
